import { useNavigate, useParams } from "react-router-dom";
import { useProcedureTypeQuery } from "../../queries";
import { Button, ButtonGroup, Dropdown, DropdownButton } from "react-bootstrap";
import { isTissueList } from "../lists";
import { useCurrentCase, useCurrentTissue } from "../../hooks";
import { Icon, fas } from "../../component-library";
import { Claims, QueryType } from "../../models";
import { useCallback, useMemo, useState } from "react";
import { usePermissions } from "../../contexts";
import { ProcedureConfigModal } from "../procedures";

export function TissueHeader() {
    const { listSlug, tissueId, territoryId } = useParams<{ listSlug: string; tissueId: string; territoryId: string; }>();
    const { caseId } = useCurrentCase();
    const navigate = useNavigate();
    const { tissueNumber, procedures, procedureName, procedure, tissue } = useCurrentTissue();
    const [showNewProcedureModal, setShowNewProcedureModal] = useState<boolean>(false);

    const {
        listQuery: { data: procedureTypes },
    } = useProcedureTypeQuery();
    const tissueTitle = tissueNumber ?? "Tissue";
    const { hasPermission } = usePermissions();

    const isTissue = isTissueList(listSlug);

    const getProcedureName = useCallback(
        (procedureTypeId: number) => {
            const procedureType = procedureTypes?.find((type) => type.id === procedureTypeId);
            return procedureType?.name ?? "Unknown";
        },
        [procedureTypes]
    );

    const listUrl = territoryId ? `/lists/${listSlug}/territory/${territoryId}` : `/lists/${listSlug}`;
    const baseUrl = isTissue
        ? `${listUrl}/tissues/${tissueId}`
        : `${listUrl}/cases/${caseId}/tissues/${tissueId}`;

    const isActive = (path: string) => window.location.pathname.includes(path);

    const proceduresTitle = useMemo(() => {
        if (!procedures.length) return "Procedures";

        if (procedureName) {
            return `Procedures (${procedures.length}): ${procedureName}`;
        }

        return `Procedures (${procedures.length})`;
    }, [procedureName, procedures.length]);

    return (
        <div className="tissue-detail-header-container">
            <div className="tissue-detail-header">
                <div className="tissue-detail-header-title">{tissueTitle}</div>
                <div className="tissue-detail-header-links">
                    <ButtonGroup size="sm">
                        <Button
                            variant="outline-primary"
                            onClick={() => navigate(`${baseUrl}/placement`)}
                            active={isActive("placement")}
                            className="tissue-header-link"
                        >
                            Placement
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={() => navigate(`${baseUrl}/grading`)}
                            active={isActive("grading")}
                            className="tissue-header-link"
                        >
                            Initial Grading
                        </Button>
                        <DropdownButton
                            as={ButtonGroup}
                            className="tissue-header-dropdown"
                            variant="outline-primary"
                            title={proceduresTitle}
                            id="tissue-procedures"
                        >
                            {procedures.map((p) => (
                                <Dropdown.Item
                                    key={p.id}
                                    onClick={() => navigate(`${baseUrl}/procedures/${p.id}`)}
                                    active={p.id === procedure?.id}
                                >
                                    {getProcedureName(p.procedureTypeId)}
                                </Dropdown.Item>
                            ))}
                            {hasPermission(Claims.CreateProcedure) && (
                                <>
                                    {!!procedures.length && <Dropdown.Divider />}
                                    <Dropdown.Item onClick={() => setShowNewProcedureModal(true)}>
                                        <Icon icon={fas.faPlus} /> New Procedure
                                    </Dropdown.Item>
                                </>
                            )}
                        </DropdownButton>
                        <Button
                            variant="outline-primary"
                            onClick={() => navigate(`${baseUrl}/shipments`)}
                            active={isActive("shipments")}
                            className="tissue-header-link"
                        >
                            Shipments
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            <ProcedureConfigModal
                show={showNewProcedureModal}
                onHide={() => setShowNewProcedureModal(false)}
                mode="create"
            />
        </div>
    );
}
